import { makeAutoObservable } from "mobx";

import { ComputerAgentDataStatus, MessageRole, ToolCallStatus } from "../types";
import {
  findComputerAgentMessageIndex as findComputerAgentMessageIndexInMessages,
  findToolCallToUpdate as findToolCallToUpdateInMessages,
} from "../utils/messageHelpers";

import { ChatMessage } from "./types";
import { SessionStore } from "./session-store";

interface ChatStreamCursor {
  currentTaskId: string | null;
  assistantMessageIndex: number;
  currentMessageRole: MessageRole.MASTER_VIBE_PROMPT | MessageRole.ASSISTANT;
  isFirstDelta: boolean;
  lastUserMessageId: string | null;
}

const createDefaultCursor = (): ChatStreamCursor => ({
  currentTaskId: null,
  assistantMessageIndex: -1,
  currentMessageRole: MessageRole.ASSISTANT,
  isFirstDelta: true,
  lastUserMessageId: null,
});

export class MessageStore {
  private messagesByChatId = new Map<string, ChatMessage[]>();
  private cursorsByChatId = new Map<string, ChatStreamCursor>();

  constructor(private sessions: SessionStore) {
    makeAutoObservable(this, {
      getMessages: false,
      getCurrentTaskId: false,
      getAssistantMessageIndex: false,
      getCurrentMessageRole: false,
      getIsFirstDelta: false,
      getLastUserMessageId: false,
      findToolCallToUpdate: false,
      findComputerAgentMessageIndex: false,
    });
  }

  private getCursor(chatId: string): ChatStreamCursor {
    let cursor = this.cursorsByChatId.get(chatId);
    if (!cursor) {
      cursor = createDefaultCursor();
      this.cursorsByChatId.set(chatId, cursor);
    }
    return cursor;
  }

  get messagesByChatIdMap(): Map<string, ChatMessage[]> {
    return this.messagesByChatId;
  }

  get currentMessages(): ChatMessage[] {
    const chatId = this.sessions.currentChatId;
    if (!chatId) {
      return [];
    }
    return this.getMessages(chatId);
  }

  getMessages(chatId: string): ChatMessage[] {
    return this.messagesByChatId.get(chatId) || [];
  }

  getCurrentTaskId(chatId: string): string | null {
    return this.getCursor(chatId).currentTaskId;
  }

  setCurrentTaskId(chatId: string, taskId: string | null) {
    this.getCursor(chatId).currentTaskId = taskId;
  }

  getAssistantMessageIndex(chatId: string): number {
    return this.getCursor(chatId).assistantMessageIndex;
  }

  setAssistantMessageIndex(chatId: string, index: number) {
    this.getCursor(chatId).assistantMessageIndex = index;
  }

  getCurrentMessageRole(chatId: string): MessageRole.MASTER_VIBE_PROMPT | MessageRole.ASSISTANT {
    return this.getCursor(chatId).currentMessageRole;
  }

  setCurrentMessageRole(
    chatId: string,
    role: MessageRole.MASTER_VIBE_PROMPT | MessageRole.ASSISTANT,
  ) {
    this.getCursor(chatId).currentMessageRole = role;
  }

  getIsFirstDelta(chatId: string): boolean {
    return this.getCursor(chatId).isFirstDelta;
  }

  setIsFirstDelta(chatId: string, value: boolean) {
    this.getCursor(chatId).isFirstDelta = value;
  }

  getLastUserMessageId(chatId: string): string | null {
    return this.getCursor(chatId).lastUserMessageId;
  }

  setLastUserMessageId(chatId: string, id: string | null) {
    this.getCursor(chatId).lastUserMessageId = id;
  }

  initializeChat(chatId: string) {
    if (!this.messagesByChatId.has(chatId)) {
      this.messagesByChatId.set(chatId, []);
    }
  }

  setMessages(chatId: string, messages: ChatMessage[]) {
    this.messagesByChatId.set(chatId, messages);
  }

  deleteChat(chatId: string) {
    this.messagesByChatId.delete(chatId);
    this.cursorsByChatId.delete(chatId);
  }

  clear(chatId: string) {
    this.messagesByChatId.set(chatId, []);
    this.cursorsByChatId.delete(chatId);
  }

  clearAll() {
    this.messagesByChatId.clear();
    this.cursorsByChatId.clear();
  }

  resetState(chatId: string) {
    this.cursorsByChatId.set(chatId, createDefaultCursor());
  }

  push(chatId: string, message: ChatMessage) {
    const messages = this.getMessages(chatId);
    const cursor = this.getCursor(chatId);
    const messageWithTaskId =
      cursor.currentTaskId && !message.taskId
        ? { ...message, taskId: cursor.currentTaskId }
        : message;
    this.messagesByChatId.set(chatId, [...messages, messageWithTaskId]);
  }

  set(chatId: string, index: number, message: ChatMessage) {
    const messages = [...this.getMessages(chatId)];
    messages[index] = message;
    this.messagesByChatId.set(chatId, messages);
  }

  updateContent(chatId: string, index: number, updater: (content: string) => string) {
    const messages = [...this.getMessages(chatId)];
    const current = messages[index];
    if (!current) {
      return;
    }
    messages[index] = { ...current, content: updater(current.content) };
    this.messagesByChatId.set(chatId, messages);
  }

  updateAt(chatId: string, index: number, updater: (message: ChatMessage) => ChatMessage) {
    const messages = [...this.getMessages(chatId)];
    const current = messages[index];
    if (!current) {
      return;
    }
    messages[index] = updater(current);
    this.messagesByChatId.set(chatId, messages);
  }

  completeRunningToolCalls(chatId: string) {
    const messages = this.getMessages(chatId);
    const updated = messages.map(msg => {
      if (msg.role === MessageRole.TOOL_CALL && msg.toolCall?.status === ToolCallStatus.RUNNING) {
        return {
          ...msg,
          toolCall: {
            ...msg.toolCall,
            status: ToolCallStatus.COMPLETED,
            completedAt: Date.now(),
          },
        };
      }
      return msg;
    });
    this.messagesByChatId.set(chatId, updated);
  }

  completeRunningComputerAgents(chatId: string) {
    const messages = this.getMessages(chatId);
    const updated = messages.map(msg => {
      if (
        msg.role === MessageRole.COMPUTER_AGENT &&
        msg.computerAgent?.status === ComputerAgentDataStatus.RUNNING
      ) {
        return {
          ...msg,
          computerAgent: {
            ...msg.computerAgent,
            status: ComputerAgentDataStatus.COMPLETED,
          },
        };
      }
      return msg;
    });
    this.messagesByChatId.set(chatId, updated);
  }

  removeTrailingEmptyAssistants(chatId: string) {
    const messages = this.getMessages(chatId);
    let end = messages.length;
    while (
      end > 0 &&
      messages[end - 1]?.role === MessageRole.ASSISTANT &&
      !(messages[end - 1]?.content || "").trim()
    ) {
      end -= 1;
    }
    if (end === messages.length) {
      return;
    }
    this.messagesByChatId.set(chatId, messages.slice(0, end));
  }

  findToolCallToUpdate(chatId: string, toolCallId: string): number {
    return findToolCallToUpdateInMessages(this.getMessages(chatId), toolCallId);
  }

  findComputerAgentMessageIndex(chatId: string): number {
    return findComputerAgentMessageIndexInMessages(this.getMessages(chatId));
  }

  transferMessages(fromChatId: string, toChatId: string) {
    const messages = this.messagesByChatId.get(fromChatId);
    if (messages) {
      this.messagesByChatId.set(toChatId, messages);
      this.messagesByChatId.delete(fromChatId);
    }

    const cursor = this.cursorsByChatId.get(fromChatId);
    if (cursor) {
      this.cursorsByChatId.set(toChatId, cursor);
      this.cursorsByChatId.delete(fromChatId);
    }
  }
}
